import type React from "react";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils/index";
import type { PublicCalendarLinks } from "../../../../_logic/calendar-sync/public-calendar-access";

interface AddToCalendarLinksProps {
  /** The event's public calendar links (`publicCalendarLinks` from `SchedulePublicDataProvider`). */
  links: PublicCalendarLinks;
  /** Extra classes merged onto each pill, for the two slightly different sizing contexts it's used in. */
  className?: string;
  /** Short labels ("Google", "Apple") for the cramped full-page header; screen readers get the full name. */
  isCompact?: boolean;
}

const PILL_CLASSES =
  "inline-flex touch-manipulation items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50";

/**
 * "Add to calendar" pills for the public schedule: Google Calendar, and Apple Calendar via the
 * `webcal://` feed. The second one exists for phones: a link tapped in Slack opens Slack's in-app
 * browser, which isn't signed in to Google, while `webcal://` hands off to the device's calendar app.
 * Only rendered once the event's calendar has been made public. Shared by `ScheduleRenderer` (inline
 * section) and `FocusedScheduleView` (full-page header) so the two surfaces don't drift.
 *
 * @param props - See AddToCalendarLinksProps.
 * @returns The two links, as siblings for the caller's flex row.
 */
export function AddToCalendarLinks({
  links,
  className,
  isCompact = false,
}: AddToCalendarLinksProps): React.ReactElement {
  return (
    <>
      <a href={links.googleUrl} target="_blank" rel="noopener noreferrer" className={cn(PILL_CLASSES, className)}>
        <Calendar className="h-4 w-4" aria-hidden="true" />
        {isCompact ? <span aria-hidden="true">Google</span> : "Google Calendar"}
        <span className="sr-only">{isCompact ? "Add to Google Calendar " : ""}(opens in a new tab)</span>
      </a>
      {/* No target: webcal:// opens the calendar app, and a new tab would be left blank. */}
      <a href={links.feedUrl} className={cn(PILL_CLASSES, className)}>
        <Calendar className="h-4 w-4" aria-hidden="true" />
        {isCompact ? <span aria-hidden="true">Apple</span> : "Apple Calendar"}
        {isCompact && <span className="sr-only">Add to Apple Calendar</span>}
      </a>
    </>
  );
}
